import { ElementRef, ForwardRefExoticComponent, forwardRef, useId } from 'react'
import {
  FormControl,
  FormControlProps,
  FormControlTypeMap
} from '../../internal-components/FormControl/FormControl'
import { FormHelperText } from '@mui/material'
import { FormLabel } from '../../internal-components/FormLabel/FormLabel'
import {
  default as MuiRadioGroup,
  RadioGroupProps as MuiRadioGroupProps
} from '@mui/material/RadioGroup'
import { Radio, RadioColor, RadioProps } from '../../internal-components/Radio/Radio'

export type RadioGroupProps = Pick<
  MuiRadioGroupProps,
  'defaultValue' | 'name' | 'onChange' | 'row' | 'value' | 'children'
> & {
  /**
   * Props applied to the [`FormControl`](https://mui.com/material-ui/api/form-control/) element.
   */
  formControlProps?: Omit<FormControlProps, 'children' | 'variant'>
  /**
   * helperText is applied to the `FormHelperText` component
   */
  helperText?: string
  /**
   * label is applied to the `FormLabel` component
   */
  label: string
  /**
   * Array of options to be rendered as Radio buttons
   */
  options?: RadioProps[]
  /**
   * color is applied to the `Radio` component
   * @default 'primary'
   */
  radioColor?: RadioColor
  hiddenLabel?: boolean
}

/**
 * The `RadioGroup` is a helpful wrapper used to group `Radio` components that provides an easier API, and proper keyboard accessibility to the group.
 *
 * ## Advanced Configuration
 *
 * It's important to understand that the `RadioGroup` is a simple abstraction
 * on top of the following components:
 *
 * *   [FormControl](https://mui.com/material-ui/api/form-control/)
 * *   [FormLabel](https://mui.com/material-ui/api/form-label/)
 * *   [RadioGroup](https://mui.com/material-ui/api/radio-group/)
 * *   [FormHelperText](https://mui.com/material-ui/api/form-helper-text/)
 *
 * If you wish to alter the props applied to the `FormControl` element, you can do so as follows:
 *
 * ```jsx
 * const formControlProps = {
 *   error: true,
 *   required:true
 * };
 *
 * return <RadioGroup label='Choose an Option' formControlProps={formControlProps} />;
 * ```
 */
export const RadioGroup: ForwardRefExoticComponent<RadioGroupProps> = forwardRef<
  ElementRef<FormControlTypeMap['defaultComponent']>,
  RadioGroupProps
>(
  (
    { helperText, children, label, options, formControlProps, hiddenLabel, radioColor, ...rest },
    ref
  ) => {
    const id = useId()
    const labelId = `radio-group-${id}-label`
    const helperTextId = `radio-group-${id}-helper-text`
    const radioGroupProps = {
      ...rest,
      'aria-labelledby': !hiddenLabel ? labelId : undefined,
      'aria-label': hiddenLabel ? label : undefined,
      ...(!!helperText && { 'aria-describedby': helperTextId })
    }

    return (
      <FormControl ref={ref} {...formControlProps} variant="standard">
        {!hiddenLabel && <FormLabel id={labelId}>{label}</FormLabel>}
        <MuiRadioGroup {...radioGroupProps}>
          {options?.map(({ label: radioLabel, value, ...rest }) => (
            <Radio
              key={`${value}-${radioLabel}`}
              label={radioLabel}
              value={value}
              color={radioColor}
              {...rest}
            />
          ))}
          {children}
        </MuiRadioGroup>
        {helperText && <FormHelperText id={helperTextId}>{helperText}</FormHelperText>}
      </FormControl>
    )
  }
)
RadioGroup.displayName = 'RadioGroup'
